import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { X, CheckCircle2, AlertTriangle, Info } from "lucide-react"

type ToastVariant = "success" | "error" | "info"

type Toast = {
  id: number
  message: string
  variant: ToastVariant
}

type ToastContextValue = {
  toast: (message: string, variant?: ToastVariant) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DURATION = 4000

const VARIANT_STYLES: Record<ToastVariant, { bg: string; icon: typeof CheckCircle2 }> = {
  success: { bg: "bg-emerald-600 dark:bg-emerald-700", icon: CheckCircle2 },
  error: { bg: "bg-red-600 dark:bg-red-700", icon: AlertTriangle },
  info: { bg: "bg-stone-700 dark:bg-stone-600", icon: Info },
}

function ToastItem({ toast: t, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const { bg, icon: Icon } = VARIANT_STYLES[t.variant]
  const [exiting, setExiting] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    timerRef.current = setTimeout(() => setExiting(true), TOAST_DURATION - 300)
    const removeTimer = setTimeout(() => onDismiss(t.id), TOAST_DURATION)
    return () => {
      clearTimeout(timerRef.current)
      clearTimeout(removeTimer)
    }
  }, [t.id, onDismiss])

  return (
    <div
      role="alert"
      className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium max-w-sm pointer-events-auto transition-all duration-300 ${bg} ${exiting ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      <span className="flex-1 min-w-0">{t.message}</span>
      <button
        onClick={() => onDismiss(t.id)}
        className="shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((message: string, variant: ToastVariant = "info") => {
    const id = nextId.current++
    setToasts((prev) => [...prev, { id, message, variant }])
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[10001] flex flex-col gap-2 pointer-events-none" aria-live="polite">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within ToastProvider")
  return ctx
}
